import { SpecFilterGroup } from '@/components/catalogue/products/useFilteredProducts';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { Manufacturer } from '@/services/api/contracts/catalog/types';
import { useCatalogueExplorerFilterService } from '@/services/catalogue/useCatalogueExplorerFilterService';
import { FunctionComponent, Suspense, useState } from 'react';
import CatalogueExplorerFilterManufacturers from './CatalogueExplorerFilterManufacturers';
import CatalogueExplorerSpecFilter from './CatalogueExplorerSpecFilter';
import { SpecGroup, useBuildGroupedSpecifications } from './useBuildGroupedSpecifications';

const buildSpecQueries = (groupedSpecs: SpecGroup[], checkedSpecs: string[]) =>
  groupedSpecs
    .map(group => {
      return { queries: group.specs.filter(s => checkedSpecs.includes(s)) };
    })
    .filter(f => f.queries.length > 0);

export interface CatalogueExplorerFilterCallback {
  specs?: SpecFilterGroup[];
  manufacturers?: string[];
}

const CatalogueExplorerFilterModal: FunctionComponent<
  ModalProps<{ categoryId?: string; onFilterCallback: (props: CatalogueExplorerFilterCallback) => void }>
> = props => {
  const CatalogueExplorerFilter = () => {
    const categoryId = props.props?.categoryId;
    const onFilterCallback = props.props?.onFilterCallback;
    if (!onFilterCallback) throw new Error('Missing filter props');

    const groupedSpecifications = useBuildGroupedSpecifications(categoryId);

    const [specFilters, setSpecFilters, manufacturerFilters, setManufacturersFilters] =
      useCatalogueExplorerFilterService(state => [
        state.specFilters,
        state.setSpecFilter,
        state.manufacturerFilters,
        state.setManufacturerFilters
      ]);

    const [selectedSpecs, setSelectedSpecs] = useState<string[]>();

    const [selectedManufacturers, setSelectedManufacturers] = useState<Manufacturer[]>();

    const onApply = () => {
      const queries = buildSpecQueries(groupedSpecifications, selectedSpecs ?? []);

      const specs = selectedSpecs === undefined ? specFilters : queries;
      const manufacturers = selectedManufacturers === undefined ? manufacturerFilters : selectedManufacturers;
      setSpecFilters(specs);
      setManufacturersFilters(manufacturers);
      onFilterCallback({ specs, manufacturers: manufacturers.map(item => item.id) });
      props.close();
    };

    return (
      <Card className="w-full p-8 space-y-2">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-bold">Filter Products</h1>
        </Modal.Controls>

        <div className="space-y-6">
          <CatalogueExplorerSpecFilter categoryId={categoryId} onChange={setSelectedSpecs} />
          <CatalogueExplorerFilterManufacturers onChange={setSelectedManufacturers} />
          <div className="flex gap-2 justify-between">
            <Button onClick={props.close}>Cancel</Button>
            <Button type="button" variant={ButtonVariant.Primary} onClick={onApply}>
              Apply
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-3xl" {...props}>
      <Suspense fallback={<Loading />}>
        <CatalogueExplorerFilter />
      </Suspense>
    </Modal>
  );
};

export default CatalogueExplorerFilterModal;
